import { Box, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import React, { Dispatch, SetStateAction, useState } from 'react';

import { InvoiceTemplateSelection } from '../../types';

interface InvoiceTemplateSelectionProps {
	allInvoiceTemplates: InvoiceTemplateSelection[];
	handleChange: (e: any, setStateAction: any) => void;
	monthlySelection: InvoiceTemplateSelection;
	carryoverSelection: InvoiceTemplateSelection;
	validationSelection: InvoiceTemplateSelection;
	hotelStorageSelection: InvoiceTemplateSelection;
	officeRetailSelection: InvoiceTemplateSelection;
	miscSelection: InvoiceTemplateSelection;
	setMonthlySelection: Dispatch<SetStateAction<InvoiceTemplateSelection>>;
	setCarryoverSelection: Dispatch<SetStateAction<InvoiceTemplateSelection>>;
	setValidationSelection: Dispatch<SetStateAction<InvoiceTemplateSelection>>;
	setHotelStorageSelection: Dispatch<SetStateAction<InvoiceTemplateSelection>>;
	setOfficeRetailSelection: Dispatch<SetStateAction<InvoiceTemplateSelection>>;
	setMiscSelection: Dispatch<SetStateAction<InvoiceTemplateSelection>>;
	editMode: boolean;
	dataLoaded: boolean;
}

export const InvoiceTemplateSelections = ({
	allInvoiceTemplates = [],
	handleChange,
	monthlySelection,
	carryoverSelection,
	validationSelection,
	hotelStorageSelection,
	officeRetailSelection,
	miscSelection,
	setMonthlySelection,
	setCarryoverSelection,
	setValidationSelection,
	setHotelStorageSelection,
	setOfficeRetailSelection,
	setMiscSelection,
	editMode,
	dataLoaded,
}: InvoiceTemplateSelectionProps): React.JSX.Element => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedInput, setSelectedInput] = useState('');

	const handleOpen = (event, input) => {
		setSelectedInput(input);
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
		setSelectedInput('');
	};

	const downloadPreview = (e, templateName, inputType) => {
		e.stopPropagation();
		let filePath = '/defaultTemplate.pdf';
		switch (templateName) {
			case 'Default PC Invoice':
				if (inputType === 'Validation') {
					filePath = '/defaultTemplateValidation.pdf';
				}
				if (inputType === 'Carryover') {
					filePath = '/defaultTemplateCarryover.pdf';
				}
				break;
			case 'Template 2':
				filePath = '/template2Example.pdf';
				break;
			case 'Template 3':
				filePath = '/template3Example.pdf';
				break;
			default:
				break;
		}
		const downloadLink = document.createElement('a');
		downloadLink.href = filePath;
		downloadLink.download = templateName.toLowerCase().replace(/\s+/g, '_');
		downloadLink.click();
	};

	if (!dataLoaded) {
		return (
			<Box
				style={{
					width: '100%',
					height: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
				className='invoice-format-tab-loading'
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Grid container spacing={5}>
			<Grid item xs={4}>
				<FormControl sx={{ width: '100%' }}>
					<InputLabel id='monthly-invoice-format-label'>Monthly Invoice Format</InputLabel>
					<Select
						label='Monthly Invoice Format'
						labelId='monthly-invoice-format-label'
						name='monthlyInvoiceFormat'
						value={monthlySelection?.id || ''}
						onChange={(e) => handleChange(e, setMonthlySelection)}
						disabled={!editMode}
						onOpen={(event) => handleOpen(event, 'Monthly')}
						onClose={handleClose}
					>
						{allInvoiceTemplates
							.filter((ait) => ait.invoiceType === 'Monthly')
							.map((m) => (
								<MenuItem style={{ zIndex: 9998 }} key={m.id} value={m.id}>
									<div
										style={{
											width: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											zIndex: 9998,
										}}
									>
										{m.templateName}
										{anchorEl && selectedInput === 'Monthly' ? (
											<span
												className='preview-link'
												onClick={(event) => downloadPreview(event, m.templateName, 'Monthly')}
											>
												Preview
											</span>
										) : null}
									</div>
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={4}>
				<FormControl sx={{ width: '100%' }}>
					<InputLabel id='carryover-invoice-format-label'>Carryover Invoice Format</InputLabel>
					<Select
						label='Carryover Invoice Format'
						labelId='carryover-invoice-format-label'
						name='carryoverInvoiceFormat'
						value={carryoverSelection?.id || ''}
						onChange={(e) => handleChange(e, setCarryoverSelection)}
						disabled={!editMode}
						onOpen={(event) => handleOpen(event, 'Carryover')}
						onClose={handleClose}
					>
						{allInvoiceTemplates
							.filter((ait) => ait.invoiceType === 'Carryover')
							.map((t) => (
								<MenuItem style={{ zIndex: 9998 }} key={t.id} value={t.id}>
									<div
										style={{
											width: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											zIndex: 9998,
										}}
									>
										{t.templateName}
										{anchorEl && selectedInput === 'Carryover' ? (
											<span
												className='preview-link'
												onClick={(event) => downloadPreview(event, t.templateName, 'Carryover')}
											>
												Preview
											</span>
										) : null}
									</div>
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={4}>
				<FormControl sx={{ width: '100%' }}>
					<InputLabel id='validation-invoice-format-label'>Validation Invoice Format</InputLabel>
					<Select
						label='Validation Invoice Format'
						labelId='validation-invoice-format-label'
						name='validationInvoiceFormat'
						value={validationSelection?.id || ''}
						onChange={(e) => handleChange(e, setValidationSelection)}
						disabled={!editMode}
						onOpen={(event) => handleOpen(event, 'Validation')}
						onClose={handleClose}
					>
						{allInvoiceTemplates
							.filter((ait) => ait.invoiceType === 'Validation')
							.map((v) => (
								<MenuItem style={{ zIndex: 9998 }} key={v.id} value={v.id}>
									<div
										style={{
											width: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											zIndex: 9998,
										}}
									>
										{v.templateName}
										{anchorEl && selectedInput === 'Validation' ? (
											<span
												className='preview-link'
												onClick={(event) =>
													downloadPreview(event, v.templateName, 'Validation')
												}
											>
												Preview
											</span>
										) : null}
									</div>
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</Grid>

			<Grid item xs={4}>
				<FormControl sx={{ width: '100%' }}>
					<InputLabel id='hotel-storage-invoice-format-label'>Hotel Storage Invoice Format</InputLabel>
					<Select
						label='Hotel Storage Invoice Format'
						labelId='hotel-storage-invoice-format-label'
						name='hotelStorageInvoiceFormat'
						value={hotelStorageSelection?.id || ''}
						onChange={(e) => handleChange(e, setHotelStorageSelection)}
						disabled={!editMode}
						onOpen={(event) => handleOpen(event, 'Hotel Storage')}
						onClose={handleClose}
					>
						{allInvoiceTemplates
							.filter((ait) => ait.invoiceType === 'Hotel Storage')
							.map((hs) => (
								<MenuItem style={{ zIndex: 9998 }} key={hs.id} value={hs.id}>
									<div
										style={{
											width: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											zIndex: 9998,
										}}
									>
										{hs.templateName}
										{anchorEl && selectedInput === 'Hotel Storage' ? (
											<span
												className='preview-link'
												onClick={(event) =>
													downloadPreview(event, hs.templateName, 'Hotel Storage')
												}
											>
												Preview
											</span>
										) : null}
									</div>
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={4}>
				<FormControl sx={{ width: '100%' }}>
					<InputLabel id='office-Retail-invoice-format-label'>Office/Retail Invoice Format</InputLabel>
					<Select
						label='Office/Retail Invoice Format'
						labelId='office-Retail-invoice-format-label'
						name='officeRetailInvoiceFormat'
						value={officeRetailSelection?.id || ''}
						onChange={(e) => handleChange(e, setOfficeRetailSelection)}
						disabled={!editMode}
						onOpen={(event) => handleOpen(event, 'Office Retail')}
						onClose={handleClose}
					>
						{allInvoiceTemplates
							.filter((ait) => ait.invoiceType === 'Office Retail')
							.map((or) => (
								<MenuItem style={{ zIndex: 9998 }} key={or.id} value={or.id}>
									<div
										style={{
											width: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											zIndex: 9998,
										}}
									>
										{or.templateName}
										{anchorEl && selectedInput === 'Office Retail' ? (
											<span
												className='preview-link'
												onClick={(event) => downloadPreview(event, or.templateName, 'Office')}
											>
												Preview
											</span>
										) : null}
									</div>
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={4}>
				<FormControl sx={{ width: '100%' }}>
					<InputLabel id='miscellaneous-invoice-format-label'>Miscellaneous Invoice Format</InputLabel>
					<Select
						label='Miscellaneous Invoice Format'
						labelId='miscellaneous-invoice-format-label'
						name='miscellaneousInvoiceFormat'
						value={miscSelection?.id || ''}
						onChange={(e) => handleChange(e, setMiscSelection)}
						disabled={!editMode}
						onOpen={(event) => handleOpen(event, 'Miscellaneous')}
						onClose={handleClose}
					>
						{allInvoiceTemplates
							.filter((ait) => ait.invoiceType === 'Miscellaneous')
							.map((m) => (
								<MenuItem style={{ zIndex: 9998 }} key={m.id} value={m.id}>
									<div
										style={{
											width: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											zIndex: 9998,
										}}
									>
										{m.templateName}
										{anchorEl && selectedInput === 'Miscellaneous' ? (
											<span
												className='preview-link'
												onClick={(event) =>
													downloadPreview(event, m.templateName, 'Miscellaneous')
												}
											>
												Preview
											</span>
										) : null}
									</div>
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</Grid>
		</Grid>
	);
};
